import {
  BINARY_BODY_PREFIX,
  isHarBodyBase64,
  portalBodyToHarField,
  type BodyEncoding,
} from './har-body-encoding.utils';

type HarPostData = {
  mimeType: string;
  text: string;
  comment?: string;
};

type HeaderValue = string | string[] | undefined;
type Headers = Record<string, HeaderValue> | undefined;

export const BodyType = {
  NONE: 'NONE',
  HTML: 'HTML',
  JSON: 'JSON',
  XML: 'XML',
  SOAP: 'SOAP',
  FORM_DATA: 'FORM_DATA',
  MULTIPART_FORM_DATA: 'MULTIPART_FORM_DATA',
  TEXT: 'TEXT',
  GRAPHQL: 'GRAPHQL',
  PROTOBUF: 'PROTOBUF',
  UNKNOWN: 'UNKNOWN',
} as const;

export type BodyTypeValue = (typeof BodyType)[keyof typeof BodyType];

export function findHeaderValue(
  headers: Headers,
  headerName: string,
): string | undefined {
  if (!headers) return undefined;

  const lowerHeaderName = headerName.toLowerCase();

  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() === lowerHeaderName) {
      return Array.isArray(value) ? value[0] : value;
    }
  }

  return undefined;
}

export function getBodyTypeFromMimeType(mimeType: string): BodyTypeValue {
  if (mimeType.includes('text/html')) {
    return BodyType.HTML;
  }

  if (mimeType.includes('application/json')) {
    return BodyType.JSON;
  }

  if (mimeType.includes('application/soap+xml')) {
    return BodyType.SOAP;
  }

  if (mimeType.includes('application/xml') || mimeType.includes('text/xml')) {
    return BodyType.XML;
  }

  if (mimeType.includes('application/x-www-form-urlencoded')) {
    return BodyType.FORM_DATA;
  }

  if (mimeType.includes('multipart/form-data')) {
    return BodyType.MULTIPART_FORM_DATA;
  }

  if (mimeType.includes('text/plain')) {
    return BodyType.TEXT;
  }

  if (
    mimeType.includes('grpc-web') ||
    mimeType.includes('application/connect+proto') ||
    mimeType.includes('application/proto')
  ) {
    return BodyType.PROTOBUF;
  }

  return BodyType.UNKNOWN;
}

export const DEFAULT_MIME_TYPE = 'application/octet-stream';

export type HarResponseContent = {
  mimeType: string;
  text: string;
  encoding?: BodyEncoding;
  size: number;
};

function resolveHarBuildMimeType({
  headers,
  isBinaryBody,
}: {
  headers: Headers;
  isBinaryBody: boolean;
}): string {
  const headerContentType = findHeaderValue(headers, 'content-type');

  if (isBinaryBody) {
    return headerContentType || DEFAULT_MIME_TYPE;
  }

  return headerContentType ?? '';
}

export function buildHarPostData({
  body,
  bodyEncoding,
  headers,
}: {
  body: string | undefined | null;
  bodyEncoding?: BodyEncoding;
  headers: Headers;
}): HarPostData | undefined {
  if (!body) {
    return undefined;
  }

  const headerContentType = findHeaderValue(headers, 'content-type') ?? '';
  const isBinaryBody =
    bodyEncoding === 'base64' ||
    body.startsWith(BINARY_BODY_PREFIX) ||
    isHarBodyBase64({
      text: body,
      mimeType: headerContentType,
      encoding: bodyEncoding,
    });
  const contentType = resolveHarBuildMimeType({ headers, isBinaryBody });

  if (isBinaryBody) {
    const field = portalBodyToHarField({
      body,
      mimeType: contentType,
      bodyEncoding,
    });

    return {
      mimeType: field.mimeType,
      text: field.text,
      comment: field.comment,
    };
  }

  return {
    mimeType: contentType,
    text: body,
  };
}

export function buildHarResponseContent({
  body,
  bodyEncoding,
  headers,
}: {
  body: string | undefined | null;
  bodyEncoding?: BodyEncoding;
  headers: Headers;
}): HarResponseContent | undefined {
  if (!body) {
    return undefined;
  }

  const headerContentType = findHeaderValue(headers, 'content-type') ?? '';
  const isBinaryBody =
    bodyEncoding === 'base64' ||
    body.startsWith(BINARY_BODY_PREFIX) ||
    isHarBodyBase64({
      text: body,
      mimeType: headerContentType,
      encoding: bodyEncoding,
    });
  const contentType = resolveHarBuildMimeType({ headers, isBinaryBody });

  if (isBinaryBody) {
    const field = portalBodyToHarField({
      body,
      mimeType: contentType,
      bodyEncoding,
    });
    const encoding = field.encoding ?? bodyEncoding;

    return {
      mimeType: field.mimeType,
      text: field.text,
      encoding,
      size: Buffer.from(field.text, 'base64').length,
    };
  }

  return {
    mimeType: contentType,
    text: body,
    encoding: bodyEncoding,
    size: Buffer.byteLength(body, 'utf8'),
  };
}
